import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { usePostHog } from 'posthog-js/react'
import { trpc } from '@/config/trpc'
import { useOnboardingStep } from '@/lib/onboarding/tracking'
import { useUserContext } from '@/context/userLogin'
import { getAppsumoClaim, setAppsumoClaim, clearAppsumoClaim } from '@/lib/appsumoClaim'
import { Button } from '@/components/ui/shadcn/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/shadcn/card'

const PLAN_NAMES: Record<string, string> = {
  'appsumo-tier-1': 'AppSumo Tier 1',
  'appsumo-tier-2': 'AppSumo Tier 2',
  'appsumo-tier-3': 'AppSumo Tier 3',
}

// AppSumo redemption landing page. AppSumo's OAuth redirect drops buyers here
// with `?code=…`. The code is single-use, so it's exchanged immediately for a
// signed claim token that survives signup/login; once a user is
// authenticated, the claim is activated and the lifetime plan granted.
export function AppsumoPage() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const code = params.get('code')

  const { user, isPending: isUserPending, firstLogin } = useUserContext()

  const [claim, setClaim] = useState(getAppsumoClaim)
  const [error, setError] = useState<string | null>(null)
  const [activatedPlan, setActivatedPlan] = useState<string | null>(null)

  const posthog = usePostHog()
  // Only a real redemption counts as the funnel's first step; a stray visit to
  // /appsumo with nothing to redeem doesn't.
  const appsumoStep = useOnboardingStep('appsumo', !!code || !!claim)

  const exchangeCode = trpc.appsumo.exchangeCode.useMutation()
  const activate = trpc.appsumo.activate.useMutation()

  // Step 1: swap the single-use code for a claim token. The ref guards
  // against StrictMode double-mount — a second exchange with the same code
  // would fail at AppSumo.
  const exchangeStarted = useRef(false)
  useEffect(() => {
    if (!code || claim || exchangeStarted.current) return
    exchangeStarted.current = true

    exchangeCode.mutate(
      { code },
      {
        onSuccess: (result) => {
          const next = { claimToken: result.claimToken, plan: result.plan }
          setAppsumoClaim(next)
          setClaim(next)
          appsumoStep.track('appsumo_code_exchanged', { plan: result.plan })
          // Drop the consumed code from the URL so reloads don't retry it.
          navigate('/appsumo', { replace: true })
        },
        onError: (e) => {
          appsumoStep.track('appsumo_code_exchange_failed', { message: e.message })
          setError(e.message)
        },
      },
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps -- appsumoStep is recreated every render
  }, [code, claim, exchangeCode, navigate])

  // Step 2: once authenticated, bind the license to this account.
  const activateStarted = useRef(false)
  useEffect(() => {
    if (!user || !claim || activatedPlan || activateStarted.current) return
    activateStarted.current = true

    activate.mutate(
      { claimToken: claim.claimToken },
      {
        onSuccess: (result) => {
          clearAppsumoClaim()
          setActivatedPlan(result.plan)
          // Set here too, not only via usePlanPersonProperties: the funnel
          // steps right after this one should already segment by source.
          posthog.setPersonProperties({ plan_tier: result.plan, plan_source: 'appsumo' })
          appsumoStep.complete({ plan: result.plan, first_login: firstLogin })
        },
        onError: (e) => {
          // A stale claim shouldn't wedge every future page load.
          clearAppsumoClaim()
          appsumoStep.track('appsumo_activation_failed', { message: e.message })
          setError(e.message)
        },
      },
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps -- appsumoStep is recreated every render
  }, [user, claim, activatedPlan, activate])

  const needsSignIn = !!claim && !user && !isUserPending
  const signInPromptedRef = useRef(false)
  useEffect(() => {
    if (!needsSignIn || signInPromptedRef.current) return
    signInPromptedRef.current = true
    appsumoStep.track('appsumo_signin_prompted', { plan: claim?.plan })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fire once
  }, [needsSignIn])

  if (error) {
    return (
      <Shell title="Something went wrong" description={error}>
        <Button asChild>
          <a href="https://appsumo.com/products/cassi/">Return to AppSumo</a>
        </Button>
      </Shell>
    )
  }

  if (activatedPlan) {
    return (
      <Shell
        title="Your lifetime deal is active"
        description={`${PLAN_NAMES[activatedPlan] ?? activatedPlan} is now linked to your CASSI account. Welcome aboard!`}>
        <Button onClick={() => navigate(firstLogin ? '/company-setup' : '/')}>
          {firstLogin ? 'Set up your brand' : 'Go to CASSI'}
        </Button>
      </Shell>
    )
  }

  if (needsSignIn && claim) {
    return (
      <Shell
        title="Purchase verified"
        description={`Your ${PLAN_NAMES[claim.plan] ?? 'AppSumo'} license is ready. Create a CASSI account (or sign in) to activate it.`}>
        <Button onClick={() => navigate('/login')}>Continue to sign in</Button>
      </Shell>
    )
  }

  if (!code && !claim) {
    return (
      <Shell
        title="Nothing to redeem"
        description="This page activates AppSumo purchases. Start from your AppSumo account and click through to CASSI.">
        <Button asChild>
          <a href="https://appsumo.com/products/cassi/">Go to AppSumo</a>
        </Button>
      </Shell>
    )
  }

  return (
    <Shell
      title="Verifying your purchase…"
      description="Hold on a moment while we confirm your AppSumo license."
    />
  )
}

const Shell = ({
  title,
  description,
  children,
}: {
  title: string
  description: string
  children?: React.ReactNode
}) => (
  <section className="mx-auto mt-[12vh] max-w-md p-6">
    <Card>
      <CardHeader>
        <CardTitle className="text-xl">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      {children && <CardContent className="flex justify-end pt-2">{children}</CardContent>}
    </Card>
  </section>
)
